import React, { useState, useEffect } from "react";
import {
  getClinics,
  updateDatalensUrl,
  getCallStatsForClinic,
  startCallsExport, // Новый импорт
  getCallsExportResults, // Новый импорт
  getElevenLabsBalance, // Новый импорт
  startTranscriptionProcess,
  startAnalysisProcess,
  startRecommendationsProcess,
  checkTranscriptionStatus,
  checkAnalysisStatus,
  checkRecommendationsStatus,
  getClinicSettings, // Новый импорт для получения настроек
  updateClinicSettings, // Новый импорт для настроек
  getClinicLimits, // Новый импорт для лимитов
  updateClinicLimits, // Новый импорт для обновления лимитов
  resetClinicLimits, // Новый импорт для сброса лимитов
  getClinicEfficiencySettings, // Правильный импорт
  updateClinicEfficiencySettings, // Правильный импорт
} from "../../api";
import EfficiencyModal from "../../components/EfficiencyModal";

const ClinicManagementPage = () => {
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [datalensUrl, setDatalensUrl] = useState("");
  const [callStats, setCallStats] = useState(null);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  // Новые состояния для статистики и баланса
  const [elevenLabsBalance, setElevenLabsBalance] = useState(null);
  const [exportStats, setExportStats] = useState(null);

  // Новое состояние для метода определения администратора
  const [adminDetectionMethod, setAdminDetectionMethod] = useState("amocrm");
  const [isMethodLoading, setIsMethodLoading] = useState(false);

  // Новые состояния для лимитов клиники
  const [clinicLimits, setClinicLimits] = useState(null);
  const [isLimitsLoading, setIsLimitsLoading] = useState(false);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [limitsModalMode, setLimitsModalMode] = useState("update"); // "update" или "reset"
  const [limitsFormData, setLimitsFormData] = useState({
    monthly_limit_minutes: 3000,
    reset_current: false,
    reset_type: "both",
  });

  const [processes, setProcesses] = useState({
    sync: { status: 'idle', progress: 0 },
    transcribe: { status: 'idle', progress: 0 },
    analyze: { status: 'idle', progress: 0 },
    recommendations: { status: 'idle', progress: 0 }
  });
  const [activeProcess, setActiveProcess] = useState(null);
  const [pollingIntervals, setPollingIntervals] = useState({});

  // Новые состояния для критериев эффективности
  const [efficiencySettings, setEfficiencySettings] = useState(null);
  const [activeCallTypeTab, setActiveCallTypeTab] = useState("primary");
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [showEfficiencyModal, setShowEfficiencyModal] = useState(false);

  useEffect(() => {
    const fetchClinicsAndBalance = async () => {
      try {
        const clinicsData = await getClinics();
        console.log("Загруженные клиники:", clinicsData);
        setClinics(clinicsData);
        const balanceData = await getElevenLabsBalance();
        setElevenLabsBalance(balanceData);
      } catch (error) {
        setStatusMessage(`Ошибка при загрузке данных: ${error.message}`);
      }
    };
    fetchClinicsAndBalance();
  }, []);

  const formatDateForApi = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year}`;
  };

  const handleEfficiencySettingsClick = () => {
    if (!selectedClinic) return;
    setShowEfficiencyModal(true);
  };

  // Вспомогательные функции для управления процессами
  const setProcessStatus = (processType, status) => {
    setProcesses(prev => ({
      ...prev,
      [processType]: { ...prev[processType], status }
    }));
  };

  const updateProgress = (processType, progress) => {
    setProcesses(prev => ({
      ...prev,
      [processType]: { ...prev[processType], progress }
    }));
  };

  // Функция сброса всех состояний процессов
  const resetProcessStates = () => {
    // Очищаем все активные интервалы опроса
    Object.values(pollingIntervals).forEach(interval => {
      clearInterval(interval);
    });
    setPollingIntervals({});

    // Сбрасываем активный процесс
    setActiveProcess(null);

    // Сбрасываем все состояния процессов к idle
    setProcesses({
      sync: { status: 'idle', progress: 0 },
      transcribe: { status: 'idle', progress: 0 },
      analyze: { status: 'idle', progress: 0 },
      recommendations: { status: 'idle', progress: 0 }
    });
  };

  // Функция для отслеживания статуса процесса
  const startStatusPolling = (processType, clientId, startDate, endDate) => {
    const statusFunctions = {
      transcribe: checkTranscriptionStatus,
      analyze: checkAnalysisStatus,
      recommendations: checkRecommendationsStatus
    };

    const statusFunction = statusFunctions[processType];
    if (!statusFunction) return;

    // Очищаем предыдущий интервал если есть
    if (pollingIntervals[processType]) {
      clearInterval(pollingIntervals[processType]);
    }

    // Запускаем опрос статуса каждые 5 секунд
    const intervalId = setInterval(async () => {
      try {
        const statusData = await statusFunction(clientId, startDate, endDate);


        // Обновляем прогресс
        updateProgress(processType, statusData.progress_percentage || 0);

        // Обновляем статусное сообщение во время выполнения
        if (processType === 'recommendations') {
          // Для рекомендаций показываем статус на основе analysis_available
          if (!statusData.analysis_available) {
            setStatusMessage('Анализ рекомендаций запущен. Отслеживание прогресса...');
          }
        } else {
          // Для транскрибации и анализа используем обычную логику с прогрессом
          const progressMessages = {
            transcribe: `Транскрибация в процессе... ${statusData.progress_percentage || 0}%`,
            analyze: `Анализ звонков в процессе... ${statusData.progress_percentage || 0}%`
          };

          if (statusData.overall_status === 'running' || statusData.overall_status === 'partial') {
            setStatusMessage(progressMessages[processType] || `Процесс в работе... ${statusData.progress_percentage || 0}%`);
          }
        }

        // Проверяем завершение
        const isCompleted = processType === 'recommendations'
          ? statusData.analysis_available === true
          : statusData.overall_status === 'completed';

        if (isCompleted) {
          setProcessStatus(processType, 'completed');
          setActiveProcess(null);

          // Обновляем статусное сообщение в зависимости от процесса
          const completionMessages = {
            transcribe: 'Транскрибация звонков успешно завершена!',
            analyze: 'Анализ звонков успешно завершен!',
            recommendations: 'Анализ рекомендаций успешно завершен!'
          };
          setStatusMessage(completionMessages[processType] || 'Процесс завершен!');

          // Обновляем баланс после транскрибации
          if (processType === 'transcribe') {
            getElevenLabsBalance().then(setElevenLabsBalance).catch(console.error);
          }

          clearInterval(intervalId);
          setPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[processType];
            return newIntervals;
          });
        } else if (statusData.overall_status === 'partial' && statusData.progress_percentage === 100) {
          setProcessStatus(processType, 'completed');
          setActiveProcess(null);

          // Обновляем статусное сообщение в зависимости от процесса
          const completionMessages = {
            transcribe: 'Транскрибация звонков завершена!',
            analyze: 'Анализ звонков завершен!',
            recommendations: 'Анализ рекомендаций завершен!'
          };
          setStatusMessage(completionMessages[processType] || 'Процесс завершен!');

          // Обновляем баланс после транскрибации
          if (processType === 'transcribe') {
            getElevenLabsBalance().then(setElevenLabsBalance).catch(console.error);
          }

          clearInterval(intervalId);
          setPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[processType];
            return newIntervals;
          });
        }
      } catch (error) {
        console.error(`Ошибка опроса статуса для ${processType}:`, error);
      }
    }, 5000);

    setPollingIntervals(prev => ({ ...prev, [processType]: intervalId }));
  };

  const handleStartSync = async () => {
    if (!selectedClinic || !dateRange.from || !dateRange.to || activeProcess) return;

    setActiveProcess('sync');
    setProcessStatus('sync', 'running');
    setStatusMessage("Запуск экспорта звонков из AmoCRM...");
    setExportStats(null);

    const formattedFrom = formatDateForApi(dateRange.from);
    const formattedTo = formatDateForApi(dateRange.to);

    try {
      const exportParams = {
        client_id: selectedClinic.client_id,
        start_date: formattedFrom,
        end_date: formattedTo,
        max_pages: 10,
        detailed: true,
        max_calls: 500,
        concurrency: 5,
        filter_zero_duration: true,
      };

      const exportResult = await startCallsExport(exportParams);
      setStatusMessage(exportResult.message || "Задачи на обработку добавлены. Ожидание результатов...");

      // Запускаем поллинг для получения результатов
      const intervalId = setInterval(async () => {
        try {
          const results = await getCallsExportResults(selectedClinic.client_id, formattedFrom, formattedTo);

          if (results.processing) {
            setStatusMessage("Идет обработка звонков... Пожалуйста, подождите.");
          } else if (results.success) {
            clearInterval(intervalId);
            setProcessStatus('sync', 'completed');
            setActiveProcess(null);
            setStatusMessage(results.message);
            setExportStats(results.statistics);
            // Обновляем общую статистику звонков
            getCallStatsForClinic(selectedClinic.client_id).then(setCallStats).catch(console.error);
          }
        } catch (pollError) {
          clearInterval(intervalId);
          setProcessStatus('sync', 'error');
          setActiveProcess(null);
          setStatusMessage(`Ошибка при получении результатов экспорта: ${pollError.message}`);
        }
      }, 5000);

      setPollingIntervals(prev => ({ ...prev, sync: intervalId }));

    } catch (error) {
      setProcessStatus('sync', 'error');
      setActiveProcess(null);
      setStatusMessage(`Ошибка синхронизации: ${error.message}`);
    }
  };

  const handleStartTranscription = async () => {
    if (!selectedClinic || !dateRange.from || !dateRange.to || activeProcess) return;

    setActiveProcess('transcribe');
    setProcessStatus('transcribe', 'running');
    setStatusMessage("Запуск транскрибации звонков...");

    try {
      const formattedFrom = formatDateForApi(dateRange.from);
      const formattedTo = formatDateForApi(dateRange.to);

      await startTranscriptionProcess(selectedClinic.client_id, formattedFrom, formattedTo);
      setStatusMessage("Транскрибация запущена. Отслеживание прогресса...");

      // Запускаем отслеживание статуса
      startStatusPolling('transcribe', selectedClinic.client_id, formattedFrom, formattedTo);

    } catch (error) {
      setProcessStatus('transcribe', 'error');
      setActiveProcess(null);
      const errorMessage = typeof error === 'string' ? error : error.message || 'Неизвестная ошибка';
      setStatusMessage(`Ошибка транскрибации: ${errorMessage}`);
    }
  };

  const handleStartAnalysis = async () => {
    if (!selectedClinic || !dateRange.from || !dateRange.to || activeProcess) return;

    setActiveProcess('analyze');
    setProcessStatus('analyze', 'running');
    setStatusMessage("Запуск анализа звонков...");

    try {
      const formattedFrom = formatDateForApi(dateRange.from);
      const formattedTo = formatDateForApi(dateRange.to);

      await startAnalysisProcess(selectedClinic.client_id, formattedFrom, formattedTo);
      setStatusMessage("Анализ запущен. Отслеживание прогресса...");

      // Начинаем отслеживание статуса
      startStatusPolling('analyze', selectedClinic.client_id, formattedFrom, formattedTo);
    } catch (error) {
      setProcessStatus('analyze', 'error');
      setActiveProcess(null);
      const errorMessage = typeof error === 'string' ? error : error.message || 'Неизвестная ошибка';
      setStatusMessage(`Ошибка анализа: ${errorMessage}`);
    }
  };

  const handleStartRecommendations = async () => {
    if (!selectedClinic || !dateRange.from || !dateRange.to || activeProcess) return;

    setActiveProcess('recommendations');
    setProcessStatus('recommendations', 'running');
    setStatusMessage("Запуск анализа рекомендаций...");

    try {
      const formattedFrom = formatDateForApi(dateRange.from);
      const formattedTo = formatDateForApi(dateRange.to);

      const result = await startRecommendationsProcess(selectedClinic.client_id, formattedFrom, formattedTo);

      // Анализ рекомендаций работает синхронно - сразу показываем завершение
      if (result && result.message && result.message.includes('завершен')) {
        setProcessStatus('recommendations', 'completed');
        setActiveProcess(null);
        setStatusMessage('Анализ рекомендаций успешно завершен!');
      } else {
        // Если результат не содержит признаков завершения, запускаем поллинг
        setStatusMessage("Анализ рекомендаций запущен. Отслеживание прогресса...");
        startStatusPolling('recommendations', selectedClinic.client_id, formattedFrom, formattedTo);
      }
    } catch (error) {
      setProcessStatus('recommendations', 'error');
      setActiveProcess(null);
      const errorMessage = typeof error === 'string' ? error : error.message || 'Неизвестная ошибка';
      setStatusMessage(`Ошибка анализа рекомендаций: ${errorMessage}`);
    }
  };

  // Очистка интервалов при размонтировании компонента
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, [pollingIntervals]);

  // Загрузка метода определения администратора при выборе клиники
  useEffect(() => {
    if (selectedClinic) {
      const loadClinicSettings = async () => {
        try {
          const settings = await getClinicSettings(selectedClinic.client_id);
          setAdminDetectionMethod(settings.admin_detection_method || "amocrm");

          // Загрузка настроек эффективности
          loadEfficiencySettings(selectedClinic.client_id);
        } catch (error) {
          console.error("Ошибка при загрузке настроек:", error);
          setAdminDetectionMethod("amocrm");
        }
      };
      loadClinicSettings();
    }
  }, [selectedClinic]);

  // Обработчик изменения метода определения администратора
  const handleAdminDetectionMethodChange = async (method) => {
    if (!selectedClinic) return;

    setIsMethodLoading(true);
    try {
      await updateClinicSettings(selectedClinic.client_id, {
        admin_detection_method: method,
      });
      setAdminDetectionMethod(method);
      setStatusMessage(`Метод определения администратора изменён на: ${method === "amocrm" ? "AmoCRM" : "AI + График работы"}`);

      // Обновляем клинику в списке
      const updatedClinics = clinics.map(c =>
        c.client_id === selectedClinic.client_id
          ? { ...c, admin_detection_method: method }
          : c
      );
      setClinics(updatedClinics);
      setSelectedClinic({ ...selectedClinic, admin_detection_method: method });
    } catch (error) {
      setStatusMessage(`Ошибка при изменении метода: ${error.message}`);
    } finally {
      setIsMethodLoading(false);
    }
  };

  // Загрузка настроек эффективности
  const loadEfficiencySettings = async (clientId) => {
    setIsSettingsLoading(true);
    try {
      const response = await getClinicEfficiencySettings(clientId);
      if (response && response.data && response.data.efficiency_settings) {
        setEfficiencySettings(response.data.efficiency_settings);
      } else {
        // Дефолтные настройки
        setEfficiencySettings({
          overall: {
            effective_range: "7-10",
            ineffective_range: "1-6"
          },
          criteria: {}
        });
      }
    } catch (error) {
      console.error("Ошибка при загрузке настроек эффективности:", error);
      setStatusMessage(`Ошибка при загрузке настроек эффективности: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Обработчик изменения значения (упрощенный)
  const handleEfficiencySettingChange = (newSettings) => {
    setEfficiencySettings(newSettings);
  };

  // Сохранение настроек эффективности
  const handleSaveEfficiencySettings = async (newSettings) => {
    if (!selectedClinic) return;

    setIsSettingsLoading(true);
    try {
      await updateClinicEfficiencySettings(selectedClinic.client_id, { efficiency_settings: newSettings });
      setStatusMessage("Настройки эффективности успешно сохранены!");
      setShowEfficiencyModal(false);
    } catch (error) {
      setStatusMessage(`Ошибка при сохранении настроек: ${error.message}`);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  // Загрузка лимитов клиники
  const loadClinicLimits = async (clientId) => {
    setIsLimitsLoading(true);
    try {
      const data = await getClinicLimits(clientId);
      setClinicLimits(data);
    } catch (error) {
      console.error("Ошибка при загрузке лимитов:", error);
      setClinicLimits(null);
    } finally {
      setIsLimitsLoading(false);
    }
  };

  // Обработчик открытия модального окна обновления лимитов
  const handleOpenUpdateLimitsModal = () => {
    setLimitsModalMode("update");
    setLimitsFormData({
      monthly_limit_minutes: clinicLimits?.monthly_limit_minutes || 3000,
      reset_current: false,
      reset_type: "both",
    });
    setShowLimitsModal(true);
  };

  // Обработчик открытия модального окна сброса лимитов
  const handleOpenResetLimitsModal = () => {
    setLimitsModalMode("reset");
    setLimitsFormData({
      ...limitsFormData,
      reset_type: "both",
    });
    setShowLimitsModal(true);
  };

  // Обработчик закрытия модального окна
  const handleCloseLimitsModal = () => {
    setShowLimitsModal(false);
  };

  // Обработчик обновления лимитов
  const handleUpdateLimits = async () => {
    if (!selectedClinic) return;

    setIsLimitsLoading(true);
    try {
      const data = {
        monthly_limit_minutes: limitsFormData.monthly_limit_minutes,
      };

      if (limitsFormData.reset_current) {
        data.current_usage_minutes = 0;
      }

      await updateClinicLimits(selectedClinic.client_id, data);
      setStatusMessage("Лимиты успешно обновлены");
      await loadClinicLimits(selectedClinic.client_id);
      handleCloseLimitsModal();
    } catch (error) {
      setStatusMessage(`Ошибка при обновлении лимитов: ${error.message}`);
    } finally {
      setIsLimitsLoading(false);
    }
  };

  // Обработчик сброса лимитов
  const handleResetLimits = async () => {
    if (!selectedClinic) return;

    if (!window.confirm("Вы уверены, что хотите сбросить счётчики использования? Это действие нельзя отменить!")) {
      return;
    }

    setIsLimitsLoading(true);
    try {
      await resetClinicLimits(selectedClinic.client_id, limitsFormData.reset_type);
      setStatusMessage(`Счётчики успешно сброшены (${limitsFormData.reset_type})`);
      await loadClinicLimits(selectedClinic.client_id);
      handleCloseLimitsModal();
    } catch (error) {
      setStatusMessage(`Ошибка при сбросе лимитов: ${error.message}`);
    } finally {
      setIsLimitsLoading(false);
    }
  };

  // Функция для получения класса цвета прогресс-бара
  const getProgressBarColor = (usedMinutes, limitMinutes) => {
    if (!limitMinutes || limitMinutes === 0) return "bg-gray-400";
    const percent = (usedMinutes / limitMinutes) * 100;
    if (percent >= 85) return "bg-red-600";
    if (percent >= 60) return "bg-yellow-500";
    return "bg-green-600";
  };


  // Функция для получения текста кнопки в зависимости от состояния
  const getButtonText = (processType) => {
    const process = processes[processType];
    const texts = {
      sync: {
        idle: 'Синхронизация с AmoCRM',
        running: 'Синхронизация...',
        completed: 'Синхронизация завершена ✓',
        error: 'Ошибка синхронизации ✗',
        disabled: 'Синхронизация (заблокирована)'
      },
      transcribe: {
        idle: 'Транскрибация',
        running: 'Транскрибация...',
        completed: 'Транскрибация завершена ✓',
        error: 'Ошибка транскрибации ✗',
        disabled: 'Транскрибация (заблокирована)'
      },
      analyze: {
        idle: 'Анализ звонков',
        running: 'Анализ...',
        completed: 'Анализ завершен ✓',
        error: 'Ошибка анализа ✗',
        disabled: 'Анализ (заблокирован)'
      },
      recommendations: {
        idle: 'Анализ рекомендаций',
        running: 'Анализ рекомендаций...',
        completed: 'Анализ рекомендаций завершен ✓',
        error: 'Ошибка анализа рекомендаций ✗',
        disabled: 'Анализ рекомендаций (заблокирован)'
      }
    };

    let status = process.status;
    if (activeProcess && activeProcess !== processType) {
      status = 'disabled';
    }

    return texts[processType]?.[status] || 'Неизвестное состояние';
  };

  // Функция для получения CSS классов кнопки
  const getButtonClasses = (processType) => {
    const process = processes[processType];
    let status = process.status;
    if (activeProcess && activeProcess !== processType) {
      status = 'disabled';
    }

    const baseClasses = "py-2 px-4 rounded-md font-medium transition-colors";

    const statusClasses = {
      idle: "bg-blue-600 text-white hover:bg-blue-700",
      running: "bg-yellow-500 text-white cursor-not-allowed",
      completed: "bg-green-600 text-white hover:bg-green-700",
      error: "bg-red-600 text-white hover:bg-red-700",
      disabled: "bg-gray-400 text-white cursor-not-allowed"
    };

    return `${baseClasses} ${statusClasses[status] || statusClasses.idle}`;
  };

  // Функция для проверки, должна ли кнопка быть отключена
  const isButtonDisabled = (processType) => {
    if (!selectedClinic || !dateRange.from || !dateRange.to) return true;
    if (activeProcess && activeProcess !== processType) return true;
    const status = processes[processType].status;
    return status === 'running';
  };

  const handleSelectClinic = async (clinic) => {
    // Сбрасываем состояния процессов при выборе новой клиники
    resetProcessStates();

    setSelectedClinic(clinic);
    setDatalensUrl(clinic.datalens_dashboard_url || "");
    setStatusMessage("");
    setCallStats(null);
    setExportStats(null); // Сбрасываем статистику предыдущей клиники
    setClinicLimits(null); // Сбрасываем лимиты предыдущей клиники

    setIsStatsLoading(true);
    try {
      const stats = await getCallStatsForClinic(clinic.client_id);
      setCallStats(stats);
    } catch (error) {
      const errorMessage = error.message || 'Неизвестная ошибка';
      setStatusMessage(
        `Ошибка при загрузке статистики звонков: ${errorMessage}`
      );
    } finally {
      setIsStatsLoading(false);
    }

    // Загружаем лимиты клиники
    loadClinicLimits(clinic.client_id);
  };



  const handleUpdateUrl = async () => {
    if (!selectedClinic) {
      setStatusMessage("Сначала выберите клинику.");
      return;
    }
    if (!datalensUrl) {
      setStatusMessage("Введите URL дашборда.");
      return;
    }
    setIsUrlLoading(true);
    setStatusMessage("Обновление ссылки на дашборд Datalens...");
    try {
      await updateDatalensUrl(selectedClinic.client_id, datalensUrl);
      setStatusMessage("Ссылка на дашборд успешно обновлена!");
    } catch (error) {
      setStatusMessage(`Ошибка при обновлении URL: ${error.message}`);
    } finally {
      setIsUrlLoading(false);
    }
  };

  return (
    <div className="py-6">
      <h1 className="text-3xl font-bold text-center mb-8">
        Управление клиниками
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Левая колонка: Баланс и Список клиник */}
        <div className="md:col-span-1 space-y-6">
          {/* Карточка баланса ElevenLabs */}
          {elevenLabsBalance && (
            <div className="bg-gray-800 text-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Баланс ElevenLabs</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400">Всего токенов:</span>
                  <span className="text-lg font-bold">{elevenLabsBalance?.total_tokens?.toLocaleString() || '...'}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400">Осталось токенов:</span>
                  <span className="text-lg font-bold">{elevenLabsBalance?.remaining_tokens?.toLocaleString() || '...'}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-400">Осталось минут:</span>
                  <span className="text-lg font-bold">{elevenLabsBalance?.minutes_remaining?.toFixed(2) || '...'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Список клиник */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Список клиник</h2>
            <ul>
              {clinics.map((clinic) => (
                <li
                  key={clinic.id}
                  onClick={() => handleSelectClinic(clinic)}
                  className={`p-3 rounded-md cursor-pointer transition-colors mb-2 ${selectedClinic?.id === clinic.id
                    ? "bg-blue-500 text-white"
                    : "hover:bg-gray-100"
                    }`}
                >
                  {clinic.name}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Панель управления */}
        <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-md">
          {selectedClinic ? (
            <div>
              <h2 className="text-2xl font-bold mb-4">{selectedClinic.name}</h2>

              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-lg text-blue-800">
                  Статистика по звонкам
                </h3>
                {isStatsLoading ? (
                  <p className="text-gray-600">Загрузка статистики...</p>
                ) : callStats && callStats.count > 0 ? (
                  <p className="text-gray-700 mt-2">
                    В базе данных найдено <strong>{callStats.count}</strong>{" "}
                    записей о звонках за период с{" "}
                    <strong>{callStats.from}</strong> по{" "}
                    <strong>{callStats.to}</strong>.
                  </p>
                ) : (
                  <p className="text-gray-700 mt-2">
                    В базе данных нет записей о звонках. Вы можете запустить
                    обработку данных вручную, чтобы синхронизировать их.
                  </p>
                )}
              </div>

              {/* Статистика последнего экспорта */}
              {exportStats && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="font-semibold text-lg text-green-800">Результаты последней синхронизации</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 text-sm">
                    <p><strong>Всего найдено:</strong> {exportStats.total_found}</p>
                    <p><strong>Сохранено в БД:</strong> {exportStats.saved_to_db}</p>
                    <p><strong>Отфильтровано (короткие):</strong> {exportStats.filtered_short_calls}</p>
                    <p><strong>Отфильтровано (дубликаты):</strong> {exportStats.filtered_duplicates}</p>
                    <p><strong>Отфильтровано (нулевая длительность):</strong> {exportStats.filtered_zero_duration}</p>
                    <p><strong>Ошибки обработки:</strong> {exportStats.processing_errors}</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Настройка метода определения администратора */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="font-semibold text-lg text-purple-800 mb-3">
                    Метод определения администратора
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="adminDetectionMethod"
                        value="amocrm"
                        checked={adminDetectionMethod === "amocrm"}
                        onChange={(e) => handleAdminDetectionMethodChange(e.target.value)}
                        disabled={isMethodLoading}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-gray-700">
                        Через AmoCRM (ответственный пользователь)
                      </span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="adminDetectionMethod"
                        value="ai_schedule"
                        checked={adminDetectionMethod === "ai_schedule"}
                        onChange={(e) => handleAdminDetectionMethodChange(e.target.value)}
                        disabled={isMethodLoading}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-gray-700">
                        Через AI + график работы
                      </span>
                    </label>
                    {adminDetectionMethod === "ai_schedule" && (
                      <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
                        <p className="text-sm text-yellow-800">
                          ⚠️ <strong>Внимание!</strong> Для корректной работы необходимо заполнить график работы администраторов.
                          Незаполненные дни будут подсвечены красным.
                        </p>
                        <a
                          href="/admin/work-schedule"
                          className="inline-block mt-2 text-sm text-purple-600 hover:text-purple-800 underline"
                        >
                          Перейти к управлению графиком →
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Кнопка настройки критериев эффективности */}
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h3 className="font-semibold text-lg text-indigo-800 mb-3">
                    Критерии эффективности
                  </h3>
                  <button
                    onClick={handleEfficiencySettingsClick}
                    className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors transition-all duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Настроить критерии эффективности (19 параметров)
                  </button>
                </div>

                {/* Отображение лимитов клиники */}
                {isLimitsLoading ? (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-center text-gray-600">Загрузка лимитов...</p>
                  </div>
                ) : clinicLimits && clinicLimits.monthly_limit_minutes && clinicLimits.weekly_limit_minutes ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-lg text-green-800">
                        Лимиты транскрибации
                      </h3>
                      <div className="space-x-2">
                        <button
                          onClick={handleOpenUpdateLimitsModal}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          Пополнить баланс
                        </button>
                        <button
                          onClick={handleOpenResetLimitsModal}
                          className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                        >
                          Сбросить счётчики
                        </button>
                      </div>
                    </div>

                    {/* Месячный лимит */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">Месячный лимит</span>
                        <span className="text-gray-600">
                          {(clinicLimits.current_month_minutes || 0).toFixed(0)} / {clinicLimits.monthly_limit_minutes} мин
                          ({(clinicLimits.usage_percent || 0).toFixed(1)}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-6">
                        <div
                          className={`h-6 rounded-full transition-all ${getProgressBarColor(clinicLimits.current_month_minutes || 0, clinicLimits.monthly_limit_minutes)}`}
                          style={{
                            width: `${Math.min(clinicLimits.usage_percent || 0, 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Сброс: {clinicLimits.last_reset_date ? new Date(clinicLimits.last_reset_date).toLocaleString('ru-RU') : 'Не установлено'}
                      </p>
                    </div>

                    {/* Недельный лимит */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">Недельный лимит</span>
                        <span className="text-gray-600">
                          {(clinicLimits.current_week_minutes || 0).toFixed(0)} / {clinicLimits.weekly_limit_minutes} мин
                          ({(clinicLimits.weekly_usage_percent || 0).toFixed(1)}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-6">
                        <div
                          className={`h-6 rounded-full transition-all ${getProgressBarColor(clinicLimits.current_week_minutes || 0, clinicLimits.weekly_limit_minutes)}`}
                          style={{
                            width: `${Math.min(clinicLimits.weekly_usage_percent || 0, 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Сброс: {clinicLimits.last_week_reset_date ? new Date(clinicLimits.last_week_reset_date).toLocaleString('ru-RU') : 'Не установлено'}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Диапазон дат для обработки
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="date"
                      value={dateRange.from}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, from: e.target.value })
                      }
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                    <span>-</span>
                    <input
                      type="date"
                      value={dateRange.to}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, to: e.target.value })
                      }
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Новая система процессов с отдельными кнопками */}
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-700 mb-4">Обработка данных по этапам:</h4>

                    {/* Сетка кнопок 2x2 */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* Кнопка 1: Синхронизация с AmoCRM */}
                      <button
                        onClick={handleStartSync}
                        disabled={isButtonDisabled('sync')}
                        className={getButtonClasses('sync')}
                      >
                        {getButtonText('sync')}
                      </button>

                      {/* Кнопка 2: Транскрибация */}
                      <button
                        onClick={handleStartTranscription}
                        disabled={isButtonDisabled('transcribe')}
                        className={getButtonClasses('transcribe')}
                      >
                        {getButtonText('transcribe')}
                      </button>

                      {/* Кнопка 3: Анализ звонков */}
                      <button
                        onClick={handleStartAnalysis}
                        disabled={isButtonDisabled('analyze')}
                        className={getButtonClasses('analyze')}
                      >
                        {getButtonText('analyze')}
                      </button>

                      {/* Кнопка 4: Анализ рекомендаций */}
                      <button
                        onClick={handleStartRecommendations}
                        disabled={isButtonDisabled('recommendations')}
                        className={getButtonClasses('recommendations')}
                      >
                        {getButtonText('recommendations')}
                      </button>
                    </div>

                    {/* Информация о последовательности процессов */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Рекомендуемая последовательность:</strong> Синхронизация → Транскрибация → Анализ звонков → Анализ рекомендаций
                      </p>
                    </div>

                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    URL дашборда Datalens
                  </label>
                  <input
                    type="text"
                    value={datalensUrl}
                    onChange={(e) => setDatalensUrl(e.target.value)}
                    placeholder="https://datalens.yandex/xxxxxxxxxxxx"
                    className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                  />
                  <button
                    onClick={handleUpdateUrl}
                    disabled={isUrlLoading}
                    className="w-full mt-2 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                  >
                    {isUrlLoading ? "Обновление..." : "Обновить URL дашборда"}
                  </button>
                </div>

                {/* Раздел настройки эффективности (Кнопка открытия модального окна) */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleEfficiencySettingsClick}
                    className="w-full flex items-center justify-center p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-700 font-semibold hover:bg-indigo-100 transition-colors shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Настроить критерии эффективности
                  </button>
                </div>
              </div>

              {statusMessage && (
                <div className="mt-4 p-4 bg-gray-100 rounded-md">
                  <h3 className="font-semibold">Статус выполнения:</h3>
                  <pre className="whitespace-pre-wrap text-sm">
                    {statusMessage}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              <p>Выберите клинику из списка, чтобы начать управление.</p>
            </div>
          )}
        </div>
      </div>
      {/* Модальное окно для настройки критериев эффективности */}
      <EfficiencyModal
        show={showEfficiencyModal}
        onClose={() => setShowEfficiencyModal(false)}
        clientId={selectedClinic?.client_id}
        onSave={() => setStatusMessage("✅ Настройки эффективности сохранены")}
      />

      {/* Модальное окно для пополнения баланса */}
      {showLimitsModal && limitsModalMode === 'update' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Пополнить баланс</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Месячный лимит (минуты)
                </label>
                <input
                  type="number"
                  value={limitsFormData.monthly_limit_minutes}
                  onChange={(e) => setLimitsFormData({
                    ...limitsFormData,
                    monthly_limit_minutes: parseInt(e.target.value) || 0
                  })}
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="reset_current"
                  checked={limitsFormData.reset_current}
                  onChange={(e) => setLimitsFormData({
                    ...limitsFormData,
                    reset_current: e.target.checked
                  })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="reset_current" className="ml-2 text-sm text-gray-700">
                  Сбросить текущие счётчики использования
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowLimitsModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleUpdateLimits}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Обновить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для сброса счётчиков */}
      {showLimitsModal && limitsModalMode === 'reset' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Сбросить счётчики</h2>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="reset_type"
                  value="monthly"
                  checked={limitsFormData.reset_type === 'monthly'}
                  onChange={(e) => setLimitsFormData({
                    ...limitsFormData,
                    reset_type: e.target.value
                  })}
                  className="w-4 h-4 text-red-600"
                />
                <span className="text-gray-700">Только месячный счётчик</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="reset_type"
                  value="weekly"
                  checked={limitsFormData.reset_type === 'weekly'}
                  onChange={(e) => setLimitsFormData({
                    ...limitsFormData,
                    reset_type: e.target.value
                  })}
                  className="w-4 h-4 text-red-600"
                />
                <span className="text-gray-700">Только недельный счётчик</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="reset_type"
                  value="both"
                  checked={limitsFormData.reset_type === 'both'}
                  onChange={(e) => setLimitsFormData({
                    ...limitsFormData,
                    reset_type: e.target.value
                  })}
                  className="w-4 h-4 text-red-600"
                />
                <span className="text-gray-700">Оба счётчика</span>
              </label>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md">
              <p className="text-sm text-yellow-800">
                ⚠️ Это действие нельзя отменить. Использованные минуты будут обнулены.
              </p>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowLimitsModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleResetLimits}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClinicManagementPage;

